import { CheckboxIcon, CheckboxLabel, DropdownCheckboxWrapper, DropdownItemHotKeyWrapper, DropdownItemIconWrapper, DropdownItemLabelWrapper, DropdownItemWrapper, DropdownRadioItem, DropdownRadioItemWrapper, DropdownRadioLabel, DropdownSeparator, StyledInputCheckbox, StyledRadioIndicator, StyledRadioItem } from "./Dropdown.lib.js";
import React from "react";
import { IconArrowRight, IconCheckmark } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { Menu } from "@base-ui/react/menu";
//#region src/Dropdown/index.tsx
const menuFocus = "outline-none focus:outline-none focus-visible:outline-none";
const dropdownShadow = "shadow-[0_8px_24px_-4px_rgba(32,26,26,0.11)]";
const DropdownMenuContext = React.createContext(null);
const DropdownScrollContainer = ({ className, children }) => /* @__PURE__ */ jsx("div", {
	className: cn("max-h-96 overflow-y-auto", className),
	children
});
function DropdownItemContent({ icon, label, hotKey, arrow, disabled, activeItem, size }) {
	return /* @__PURE__ */ jsxs(DropdownItemWrapper, {
		disabled,
		size,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "flex flex-row items-center",
				children: [icon && /* @__PURE__ */ jsx(DropdownItemIconWrapper, {
					isActiveItem: activeItem === label,
					size,
					children: icon
				}), /* @__PURE__ */ jsx(DropdownItemLabelWrapper, {
					"data-cy": "dropdown-dropdownItemLabelWrapper",
					isActiveItem: activeItem === label,
					disabled,
					children: label
				})]
			}),
			arrow && /* @__PURE__ */ jsx(IconArrowRight, { className: "text-neutrals-60 h-4 w-4" }),
			hotKey && /* @__PURE__ */ jsx(DropdownItemHotKeyWrapper, { children: hotKey })
		]
	});
}
/**
* Компонент Dropdown
* Дизайн - https://www.figma.com/file/lixEouzSvWhAf04wpHXkIo/%D0%A0%D0%96%D0%94.-UI-Kit?node-id=368-4150&t=uCNcYOuABAp7jNfJ-0
*/
const DropdownItem = ({ item, state, activeItem, size, autoClose = true, onChange }) => {
	const menuCtx = React.useContext(DropdownMenuContext);
	const itemKey = item.id ?? item.value ?? "";
	const handleSelect = (value) => {
		const dropdownClick = state[itemKey];
		if (typeof dropdownClick === "function") dropdownClick(value ?? "");
	};
	if (item.children) return /* @__PURE__ */ jsxs(Menu.SubmenuRoot, { children: [/* @__PURE__ */ jsx(Menu.SubmenuTrigger, {
		disabled: item.disabled,
		onClick: (event) => {
			event.stopPropagation();
			handleSelect(item.value);
		},
		className: cn("group", menuFocus),
		children: /* @__PURE__ */ jsx(DropdownItemContent, {
			icon: item.icon,
			label: item.label,
			arrow: true,
			disabled: Boolean(item.disabled),
			activeItem,
			size
		})
	}), /* @__PURE__ */ jsx(Menu.Portal, {
		container: menuCtx?.portal ? void 0 : menuCtx?.portalContainer,
		children: /* @__PURE__ */ jsx(Menu.Positioner, {
			side: "right",
			align: "start",
			className: "relative z-[9999] min-w-52",
			children: /* @__PURE__ */ jsx(Menu.Popup, {
				finalFocus: (closeType) => closeType === "keyboard",
				className: cn("z-[9999] min-w-52 overflow-hidden rounded-xl", dropdownShadow, menuFocus),
				children: /* @__PURE__ */ jsx(Dropdown, {
					subContent: true,
					state,
					autoClose,
					scheme: item.children,
					onChange,
					size,
					activeItem
				})
			})
		})
	})] });
	return /* @__PURE__ */ jsx(Menu.Item, {
		disabled: item.disabled,
		onClick: (event) => {
			event.stopPropagation();
			handleSelect(item.value);
		},
		closeOnClick: autoClose,
		className: cn("group", menuFocus),
		children: /* @__PURE__ */ jsx(DropdownItemContent, {
			icon: item.icon,
			label: item.label,
			hotKey: item.hotKey,
			disabled: Boolean(item.disabled),
			activeItem,
			size
		})
	});
};
const DropdownCheckbox = ({ checkbox, onChange, state, size }) => {
	const checkboxValue = checkbox.value ?? checkbox.id;
	if (!checkboxValue) return null;
	const handleCheckedChange = () => {
		onChange({
			...state,
			[checkboxValue]: !state[checkboxValue]
		});
	};
	const checked = Boolean(state[checkboxValue]);
	return /* @__PURE__ */ jsx(Menu.CheckboxItem, {
		disabled: checkbox.disabled,
		className: cn("group", menuFocus),
		checked,
		onCheckedChange: handleCheckedChange,
		closeOnClick: false,
		children: /* @__PURE__ */ jsxs(DropdownCheckboxWrapper, {
			checked,
			disabled: Boolean(checkbox.disabled),
			size,
			children: [checkbox.label && /* @__PURE__ */ jsx(CheckboxLabel, {
				disabled: checkbox.disabled,
				children: checkbox.label
			}), /* @__PURE__ */ jsxs(CheckboxIcon, { children: [/* @__PURE__ */ jsx(StyledInputCheckbox, {
				disabled: checkbox.disabled,
				checked,
				onChange: (event) => event.stopPropagation(),
				name: checkboxValue,
				type: "checkbox",
				id: checkboxValue,
				value: checkboxValue
			}), checked && /* @__PURE__ */ jsx(IconCheckmark, { className: "absolute left-1/2 top-1/2 z-10 size-4 -translate-x-1/2 -translate-y-1/2 cursor-pointer text-white" })] })]
		})
	});
};
const DropdownRadio = ({ radio, autoClose, state, onChange, disabled = false, value, size }) => /* @__PURE__ */ jsx(Menu.RadioGroup, {
	value: state[value]?.toString(),
	onValueChange: (newValue) => {
		onChange({
			...state,
			[value]: newValue
		});
	},
	disabled,
	children: radio.map((radioOption) => {
		const isActive = state[value] === radioOption.value;
		return /* @__PURE__ */ jsx(DropdownRadioItem, {
			className: "group",
			disabled,
			value: radioOption.value ?? radioOption.id,
			isActive,
			closeOnClick: autoClose,
			onClick: (event) => event.stopPropagation(),
			children: /* @__PURE__ */ jsxs(DropdownRadioItemWrapper, {
				size,
				children: [/* @__PURE__ */ jsx(DropdownRadioLabel, {
					disabled,
					children: radioOption.label ?? radioOption.value
				}), /* @__PURE__ */ jsx(StyledRadioItem, {
					id: radioOption.value ?? radioOption.id,
					disabled,
					children: /* @__PURE__ */ jsx(StyledRadioIndicator, { children: /* @__PURE__ */ jsx("span", { className: "bg-neutrals-white h-2 w-2 rounded-full" }) })
				})]
			})
		}, radioOption.value);
	})
});
const RenderComponent = ({ activeItem, size, autoClose, disabled, state, onChange, option }) => {
	const handleChange = onChange ?? (() => void 0);
	return {
		ITEMS: option.type === "ITEMS" ? option.elements.map((element) => /* @__PURE__ */ jsx(DropdownItem, {
			activeItem,
			autoClose,
			item: element,
			state,
			onChange,
			size
		}, element.id ?? element.value)) : null,
		CHECKBOX: option.type === "CHECKBOX" ? option.elements.map((checkbox) => /* @__PURE__ */ jsx(DropdownCheckbox, {
			onChange: handleChange,
			checkbox,
			state,
			size
		}, checkbox.id ?? checkbox.value)) : null,
		RADIO: option.type === "RADIO" ? /* @__PURE__ */ jsx(DropdownRadio, {
			autoClose,
			onChange: handleChange,
			radio: option.elements,
			disabled: option.disabled ?? disabled,
			state,
			size,
			value: option.value
		}) : null
	}[option.type];
};
const Dropdown = React.memo(({ activeItem, trigger, disabled = false, size = "md", autoClose = true, subContent = false, portal = true, align = "center", state, scheme, onChange, onOpenChange }) => {
	const portalContainer = React.useRef(null);
	const renderDropdownOptions = scheme.map((option, index) => /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(RenderComponent, {
		autoClose,
		disabled,
		activeItem,
		option,
		state,
		onChange,
		size
	}), index !== scheme.length - 1 && /* @__PURE__ */ jsx(DropdownSeparator, {})] }, index));
	const content = /* @__PURE__ */ jsx(Menu.Positioner, {
		align,
		positionMethod: portal ? "absolute" : "fixed",
		className: "z-[9999]",
		children: /* @__PURE__ */ jsx(Menu.Popup, {
			finalFocus: (closeType) => closeType === "keyboard",
			className: cn("z-[9999] min-w-52 overflow-hidden rounded-xl", dropdownShadow, menuFocus),
			children: /* @__PURE__ */ jsx(DropdownScrollContainer, {
				className: "bg-neutrals-white flex min-w-full flex-col py-2",
				children: renderDropdownOptions
			})
		})
	});
	if (subContent) return /* @__PURE__ */ jsx(DropdownScrollContainer, {
		className: "bg-neutrals-white flex min-w-full flex-col p-0 py-2 outline-none",
		children: renderDropdownOptions
	});
	return /* @__PURE__ */ jsx(DropdownMenuContext.Provider, {
		value: {
			portal,
			portalContainer
		},
		children: /* @__PURE__ */ jsx("div", {
			className: "contents",
			children: /* @__PURE__ */ jsxs(Menu.Root, {
				onOpenChange,
				children: [
					/* @__PURE__ */ jsx(Menu.Trigger, {
						disabled,
						nativeButton: false,
						render: (triggerProps) => {
							if (!React.isValidElement(trigger)) return /* @__PURE__ */ jsx("span", {
								...triggerProps,
								className: cn("inline-flex", triggerProps.className),
								children: trigger
							});
							const triggerEl = trigger;
							const { action: _triggerAction, ...safeTriggerProps } = triggerProps;
							const originalOnMouseDown = triggerEl.props["onMouseDown"];
							const originalOnClick = triggerEl.props["onClick"];
							return React.cloneElement(triggerEl, {
								...safeTriggerProps,
								...triggerEl.props,
								...triggerEl.props["type"] === void 0 ? { type: "button" } : null,
								onMouseDown: (event) => {
									safeTriggerProps.onMouseDown?.(event);
									originalOnMouseDown?.(event);
								},
								onClick: (event) => {
									safeTriggerProps.onClick?.(event);
									originalOnClick?.(event);
								}
							});
						}
					}),
					!portal && /* @__PURE__ */ jsx("div", {
						ref: portalContainer,
						className: "contents"
					}),
					/* @__PURE__ */ jsx(Menu.Portal, {
						container: portal ? void 0 : portalContainer,
						children: content
					})
				]
			})
		})
	});
});
Dropdown.displayName = "Dropdown";
//#endregion
export { Dropdown, DropdownItem };

//# sourceMappingURL=index.js.map